import * as React from 'react'

import {
  getMerchantCategories,
  getMerchants,
  getUserBank,
  getLabels,
} from '../api/metadata'
import { useAsyncFn } from '@clara/hooks'

export interface MetadataOptions {
  /**
   * The language code in ISO 639-1 format (e.g., 'EN' for English, 'ES' for Spanish).
   */
  lang: string

  /**
   * The country code in ISO 3166-1 alpha-2 format (e.g., 'US' for United States, 'MX' for Mexico).
   */
  country_code: string

  /**
   * The currency code in ISO 4217 format (e.g., 'USD' for US Dollar, 'MXN' for Mexican Peso).
   */
  currency_code: string
}

type LoadingState = 'loading' | 'error' | 'success' | 'idle'

export function useMetadata(options: MetadataOptions) {
  const [metadata, init] = useAsyncFn(async () => {
    const response = await Promise.all([
      getMerchants({
        countryCode: options.country_code,
        merchantLang: options.lang,
      }),
      getMerchantCategories({ lang: options.lang }),
      getUserBank(),
      getLabels(),
    ])
    return response
  })

  const [merchants, categories, userbank, labels] = metadata.value ?? []

  React.useEffect(() => {
    if (Array.isArray(metadata.value) && metadata.value.length > 0) return

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadingState: LoadingState = metadata.loading
    ? 'loading'
    : metadata.error
      ? 'error'
      : Array.isArray(metadata.value) && metadata.value.length > 0
        ? 'success'
        : 'idle'

  const isLoading = loadingState === 'loading'

  return {
    options,
    merchants,
    categories,
    userbank,
    loadingState,
    isLoading,
    labels,
  }
}

export type UseMetadataReturn = ReturnType<typeof useMetadata>
